import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useAuthorizationsListQuery } from '~/queries/iam-authorizations'
import { useCreateRolesMutation } from '~/queries/user-roles'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'create-role-modal'

export interface FormValues {
  authorizations: IOption[]
  description?: string
  name: string
}

const validationSchema = Yup.object().shape({
  authorizations: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED).min(1, FORM_ERRORS.AT_LEAST_ONE),
  description: Yup.string().trim(),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onFailure: (key?: string) => void
  onSuccess: VoidFunction
}

const CreateRoleForm = ({ onClose, onFailure, onSuccess }: FormProps): ReactElement => {
  /** Local state */

  const defaultValues = {
    authorizations: [],
    description: '',
    name: '',
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: createRoleMutation } = useCreateRolesMutation()

  const { data: authorizations, isLoading: isAuthorizationsLoading } = useAuthorizationsListQuery()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const { authorizations, description, name } = values
    const authorizationsKeys = authorizations.map(({ value }) => value)

    try {
      await createRoleMutation([{ authorization_keys: authorizationsKeys, description: description || '', name }])
      onSuccess()
    } catch (error) {
      onFailure((error as IApiError).context)
    }
  }

  /** Render */

  const authorizationsOptions = authorizations?.data.map(({ key }) => ({ label: key, value: key })) || []

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          <TextInput
            {...register('name')}
            autoFocus
            error={errors.name?.message}
            label="Name"
            placeholder="Enter name"
            required
          />
          <TextArea
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter description"
          />
          <Controller
            control={control}
            name="authorizations"
            render={({ field }) => (
              <MultiSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.authorizations?.message}
                isLoading={isAuthorizationsLoading}
                label="Related authorizations"
                name="authorizations"
                options={authorizationsOptions}
                placeholder="Select authorizations"
                required
              />
            )}
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const CreateRoleModal = ({ isOpen, onClose, onFailure, onSuccess }: ModalProps): ReactElement => {
  return (
    <Modal className="create-role-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Create a new role</ModalHeader>
      <CreateRoleForm onClose={onClose} onFailure={onFailure} onSuccess={onSuccess} />
    </Modal>
  )
}

export default CreateRoleModal
